// Based on: https://stackoverflow.com/a/46203752/189937
import { Directive, AfterViewInit, OnDestroy, EventEmitter, Output, ElementRef, Inject, Input, NgZone } from '@angular/core';
import { fromEvent, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { DOCUMENT } from '@angular/common';

import { WindowRef } from '@finder/shared/services/window-ref/window-ref.service';

const DEBOUNCE_DELAY = 25;

@Directive({
    selector: '[finderInViewport]',
    standalone: true,
})
export class InViewportDirective implements AfterViewInit, OnDestroy {

    @Output() finderInViewport: EventEmitter<boolean> = new EventEmitter<boolean>(true);

    /**
     * @description Whether or not full element draw are required before trigger show (default: `false`)
     */
    @Input() requireFullElement = false;

    /**
     * @description Additional offset before triggering element show (default: `50`)
     */
    @Input() addedOffset = 50;

    subscriptionScroll: Subscription;
    subscriptionResize: Subscription;
    scrollPos: number;
    windowHeight: number;
    elementTop: number;
    elementHeight: number;

    constructor(
        private elementRef: ElementRef,
        private windowRef: WindowRef,
        private zone: NgZone,
        @Inject(DOCUMENT) private document: Document
    ) { }

    ngAfterViewInit() {
        this.subscribe();
    }

    ngOnDestroy() {
        this.unsubscribe();
    }

    private subscribe() {
        this.zone.runOutsideAngular(() => {
            this.subscriptionScroll = fromEvent(this.document, 'scroll')
                .pipe(debounceTime(DEBOUNCE_DELAY))
                .subscribe(() => {
                    this.saveDimensions();
                    this.saveScrollPos();
                    this.checkVisibility();
                });

            /* istanbul ignore next */
            this.subscriptionResize = fromEvent(this.document, 'resize')
                .pipe(debounceTime(DEBOUNCE_DELAY))
                .subscribe(() => {
                    this.saveDimensions();
                    this.checkVisibility();
                });
        });

    }

    private unsubscribe() {
        // Ignore the if just for coverage, there's no need to test unsubscribe
        /* istanbul ignore next */
        if (this.subscriptionScroll) {
            this.subscriptionScroll.unsubscribe();
        }

        // Ignore the if just for coverage, there's no need to test unsubscribe
        /* istanbul ignore next */
        if (this.subscriptionResize) {
            this.subscriptionResize.unsubscribe();
        }
    }

    private saveScrollPos() {
        this.scrollPos = this.windowRef.nativeWindow.scrollY;
        // This is not available in IE, so use fallback
        if (!this.scrollPos) {
            this.scrollPos = this.document.documentElement.scrollTop;
        }
    }

    private saveDimensions() {
        this.elementTop = this.getOffsetTop(this.elementRef.nativeElement);
        this.elementHeight = this.elementRef.nativeElement.offsetHeight;
        this.windowHeight = this.windowRef.nativeWindow.innerHeight;
    }

    /* istanbul ignore next */
    private checkVisibility() {
        if (this.isVisible()) {
            // double check dimensions (due to async loaded contents, e.g. images)
            this.saveDimensions();

            if (this.isVisible()) {
                this.unsubscribe();
                this.finderInViewport.emit(true);
            }
        }
    }

    private isVisible() {
        // If we need the full elem we add the elem height to the top,
        // otherwise just add the added offset
        const elementPos = this.requireFullElement ?
            this.elementTop + this.elementHeight + this.addedOffset :
            this.elementTop + this.addedOffset;

        return (this.scrollPos + this.windowHeight) >= elementPos;
    }

    private getOffsetTop(element: any) {
        let offsetTop = element.offsetTop || 0;

        if (element.offsetParent) {
            offsetTop += this.getOffsetTop(element.offsetParent);
        }

        return offsetTop;
    }
}
